// src/pages/Events/Dashboard.js
import React, { useState, useEffect, useMemo } from "react";
import { Box, Alert } from "@mui/material";

import PeriodSelector from "../../components/PeriodSelector";
import GroupSelector from "../Metrics/components/GroupSelector";
import FiltersTopRow from "../../components/FiltersTopRow";
import StickyFiltersBar from "../../components/StickyFiltersBar";
import EventCards from "../../components/EventCards";
import EventDetailsDialog from "../../components/EventDetailsDialog";
import BannerPlacement, { BANNER_MAX_WIDTH } from "../../components/BannerPlacement";
import { fetchServers, fetchActiveServer } from "../../services/serverService";
import { useEvents } from "../../hooks/useEvents";
import { useBusinessDay } from "../../hooks/useBusinessDay";
import { formatPeriodShort, getPeriodRange } from "../../utils/periods";
import { extractErrorMessage, isReportAccessDenied } from "../../utils/errors";
import NoReportAccess from "../../components/NoReportAccess";

// Человекочитаемый текст для ошибки загрузки событий:
// сообщение бэкенда, если оно есть, иначе общий текст
const getEventsErrorMessage = (err) => {
  const backendMessage = extractErrorMessage(err);
  const isFallback = backendMessage === err?.message || backendMessage === "Неизвестная ошибка";
  // HTML-страница от прокси (502 и т.п.) — не сообщение бэкенда
  if (backendMessage && !isFallback && !backendMessage.trim().startsWith("<")) {
    return backendMessage;
  }
  if (!err?.response) {
    return "Не удалось загрузить события: нет связи с сервером. Проверьте подключение к интернету и попробуйте ещё раз.";
  }
  return "Не удалось загрузить события: сервер вернул ошибку. Попробуйте позже.";
};

// Период для шапки окна деталей: «19.10.2026» или «01.10.2026 – 19.10.2026»
const formatPeriodLabel = ({ from, to }) => {
  if (!from || !to) return "";
  const toRu = (date) => date.split("-").reverse().join(".");
  return from === to ? toRu(from) : `${toRu(from)} – ${toRu(to)}`;
};

const ERROR_ALERT_SX = {
  mb: 2,
  width: "100%",
  backgroundColor: "rgba(255, 50, 50, 0.1)",
  border: "1px solid rgba(255, 100, 100, 0.3)",
  color: "#fff",
  whiteSpace: "pre-line", // текст iiko бывает многострочным
};

const Dashboard = ({ selectedGroup, allGroups, activeServerName, setSelectedGroup }) => {
  const [period, setPeriod] = useState(localStorage.getItem("selectedPeriod") || "today");
  const [activeServer, setActiveServerState] = useState(null);
  const [servers, setServers] = useState([]);
  const [showEventDetailsDialog, setShowEventDetailsDialog] = useState(false);
  const [selectedEventGroup, setSelectedEventGroup] = useState("");
  const [error, setError] = useState("");
  const [eventsErrorText, setEventsErrorText] = useState("");
  const [logs, setLogs] = useState([]);

  const token = localStorage.getItem("token");

  // Период — от учётного дня ресторана; пока он грузится, события не запрашиваем (пустой диапазон)
  const businessDay = useBusinessDay();
  const dateRange = useMemo(
    () => (businessDay ? getPeriodRange(period, businessDay) : { from: "", to: "" }),
    [period, businessDay]
  );

  const { loading: eventsLoading, loaded: eventsLoaded, events, eventCounts, error: eventsError } = useEvents(token, activeServer, dateRange);
  const loading = eventsLoading || !businessDay;

  // Ошибка загрузки событий приходит из хука; сбрасывается после успешной загрузки
  // Нет прав на отчёты iiko — не ошибка, а мягкая карточка вместо карточек событий
  const accessDenied = isReportAccessDenied(eventsError);
  useEffect(() => {
    setEventsErrorText(eventsError && !isReportAccessDenied(eventsError) ? getEventsErrorMessage(eventsError) : "");
  }, [eventsError]);

  // Загрузка серверов
  useEffect(() => {
    const init = async () => {
      if (!token) {
        setError("Токен не найден. Авторизуйтесь снова.");
        return;
      }
      try {
        const server = await fetchActiveServer(token);
        setActiveServerState(server);
        const serversList = await fetchServers(token);
        setServers(serversList);
      } catch (err) {
        const msg = extractErrorMessage(err);
        setError(msg);
      }
    };
    init();
  }, [token]);

  const addLog = (msg) =>
    setLogs((prev) => [...prev, `${new Date().toLocaleTimeString()}: ${msg}`]);

  const handleEventDetailsClick = (groupKey) => {
    const groupEvents = events[groupKey] || [];
    if (groupEvents.length === 0) return;
    setSelectedEventGroup(groupKey);
    setShowEventDetailsDialog(true);
  };

  const handlePeriodChange = (value) => {
    setPeriod(value);
    addLog(`Период изменён на ${value}`);
  };

  return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          color: "#fff",
          width: "100%",
        }}
      >
        {/* Ошибка */}
        {error && (
          <Alert severity="error" onClose={() => setError("")} sx={ERROR_ALERT_SX}>
            {error}
          </Alert>
        )}

        {/* Ошибка загрузки событий */}
        {eventsErrorText && (
          <Alert severity="error" onClose={() => setEventsErrorText("")} sx={ERROR_ALERT_SX}>
            {eventsErrorText}
          </Alert>
        )}

        <StickyFiltersBar>
          <FiltersTopRow>
            {selectedGroup !== undefined && allGroups && (
              <GroupSelector
                groups={allGroups}
                selectedGroup={selectedGroup || "Все"}
                onSelectGroup={setSelectedGroup}
                activeServerName={activeServerName}
                sx={{ mb: 0 }}
              />
            )}
          </FiltersTopRow>

          <PeriodSelector
            period={period}
            rangeLabel={formatPeriodShort(dateRange)}
            onChange={handlePeriodChange}
            onOpenFilters={() => addLog("Открыто меню фильтров")}
            showFilters={false}
          />
        </StickyFiltersBar>

        {/* Контейнер с баннером и карточками событий */}
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: 700, md: "100%" },
            mt: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {/* Баннер для страницы событий */}
          <BannerPlacement placement="events_top_promo" token={token} loading={loading} />

          {/* 🔹 Карточки событий */}
          {accessDenied ? (
            <NoReportAccess />
          ) : (
            <EventCards
              eventCounts={eventCounts}
              loading={loading && !eventsLoaded}
              dimmed={loading && eventsLoaded}
              handleEventDetailsClick={handleEventDetailsClick}
            />
          )}
        </Box>

        {/* 🔹 Диалог с деталями */}
        <EventDetailsDialog
          showEventDetailsDialog={showEventDetailsDialog}
          setShowEventDetailsDialog={setShowEventDetailsDialog}
          // Группу сбрасываем после анимации закрытия, иначе мелькает «таких событий нет»
          onExited={() => {
            if (!showEventDetailsDialog) setSelectedEventGroup("");
          }}
          selectedGroup={selectedEventGroup}
          events={events[selectedEventGroup] || []}
          periodLabel={formatPeriodLabel(dateRange)}
          // Период в один день — в карточках событий только время
          singleDay={Boolean(dateRange.from) && dateRange.from === dateRange.to}
          serverName={activeServerName || activeServer?.server_name || activeServer?.name || ""}
        />
      </Box>
  );
};

export default Dashboard;
